import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { BottomSheetModal, BottomSheetScrollView } from "@gorhom/bottom-sheet";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { View, Text, Button } from "@src/components";
import { BottomSheetFormInput } from "@src/components/FormInput/BottomSheetFormInput";
import SegmentedSelector from "@src/components/SegmentedSelector";
import { capitalize } from "@src/utils/capitalize";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { AppBottomSheet } from "@src/features/common/components/AppBottomSheet";

type Role = "manager" | "viewer";

export type CreateUserData = {
  email: string;
  role: Role;
};

interface CreateUserBottomSheetProps {
  bottomSheetRef: React.RefObject<BottomSheetModal | null>;
  siteName: string;
  initialSearchQuery?: string;
  onSave: (data: CreateUserData) => void;
}

export const CreateUserBottomSheet = memo(
  ({ bottomSheetRef, siteName, initialSearchQuery, onSave }: CreateUserBottomSheetProps) => {
    const { theme } = useUnistyles();
    const { bottom } = useSafeAreaInsets();

    const [email, setEmail] = useState(initialSearchQuery ?? "");
    const [role, setRole] = useState<Role>("manager");
    const [errors, setErrors] = useState<{ email?: string }>({});

    // Keep the input in sync when the parent's search query changes while the sheet is closed.
    useEffect(() => {
      setEmail(initialSearchQuery ?? "");
    }, [initialSearchQuery]);

    const options = useMemo(() => [{ title: "Manager" }, { title: "Viewer" }], []);

    const handleRoleChange = useCallback((option: string) => {
      setRole(option.toLowerCase() as Role);
    }, []);

    const handleEmailChange = useCallback((value: string) => {
      setEmail(value);
      if (value.trim()) setErrors((prev) => (prev.email ? { ...prev, email: undefined } : prev));
    }, []);

    const handleSave = useCallback(() => {
      const newErrors: { email?: string } = {};
      if (!email.trim()) newErrors.email = "Enter email address";

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }

      onSave({ email, role });
      setErrors({});
      setTimeout(() => {
        bottomSheetRef.current?.dismiss();
      }, 100);
    }, [email, role, onSave, bottomSheetRef]);

    const handleDismiss = useCallback(() => {
      setEmail(initialSearchQuery ?? "");
      setErrors({});
    }, [initialSearchQuery]);

    return (
      <AppBottomSheet
        bottomSheetRef={bottomSheetRef}
        enableDynamicSizing
        enablePanDownToClose
        keyboardBehavior="interactive"
        keyboardBlurBehavior="restore"
        android_keyboardInputMode="adjustResize"
        backgroundStyle={{ backgroundColor: theme.colors.surfaceCard }}
        onDismiss={handleDismiss}
      >
        <BottomSheetScrollView
          style={styles.container}
          contentContainerStyle={{ paddingBottom: bottom + theme.spacing["5xl"] }}
          keyboardDismissMode="on-drag"
          keyboardShouldPersistTaps="handled"
        >
          <Text textStyle="md/medium" style={styles.title}>
            Create new user for {siteName}
          </Text>

          <View gap="3xl">
            <BottomSheetFormInput
              placeholder="Email address"
              value={email}
              onChangeText={handleEmailChange}
              size="medium"
              keyboardType="email-address"
              autoCapitalize="none"
              error={errors.email}
            />
          </View>

          <View flexDirection="row" alignItems="center" justifyContent="space-between" style={styles.roleRow}>
            <Text textStyle="sm/regular">Role</Text>
            <SegmentedSelector
              optionsLabels={options}
              selectedOption={capitalize(role)}
              onSelectOption={handleRoleChange}
              containerStyle={styles.roleSelectorContainer}
              activeBoxStyle={styles.roleSelectorActiveBox}
            />
          </View>

          <Button variant="primary" label="Invite" size="md" onPress={handleSave} />
        </BottomSheetScrollView>
      </AppBottomSheet>
    );
  },
);

const styles = StyleSheet.create((theme) => ({
  container: {
    paddingHorizontal: theme.spacing["5xl"],
  },
  title: {
    marginVertical: theme.spacing["4xl"],
  },
  roleRow: {
    marginTop: theme.spacing["3xl"],
    marginBottom: theme.spacing["4xl"],
  },
  roleSelectorContainer: {
    backgroundColor: "transparent",
  },
  roleSelectorActiveBox: {
    backgroundColor: theme.colors.surface2.hover,
  },
}));
